import { useState, useEffect } from "react";
import { Button, Textarea, TextInput } from "../../../common";
// @ts-ignore - api.js doesn't have TypeScript definitions
import { apiService, apiClient } from "../../../../services/api";
import SynapseKindMatrixForm, {
  SynapseKindMatrixEntry,
} from "./SynapseKindMatrixForm";
import SynapseKindNeuralProfileForm, {
  SynapseKindNeuralProfileEntry,
} from "./SynapseKindNeuralProfileForm";
import "../../synapses/Synapses.css";

type SynapseKindFormState = {
  synapseKindId: string;
  kindName: string;
  description: string;
  createDate: string;
  changeDate: string;
};

const createSynapseKindFormState = (): SynapseKindFormState => ({
  synapseKindId: "",
  kindName: "",
  description: "",
  createDate: "",
  changeDate: "",
});

const createTempId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2, 8)}-${Date.now()}`;

const createSynapseKindMatrixRow = (): SynapseKindMatrixEntry => ({
  id: createTempId("matrix"),
  synapseKindMatrixId: "",
  sourceNodeKindId: "",
  targetNodeKindId: "",
  synapseKindId: "",
  purpose: "",
  defaultSettings: "",
  scope: "ONTOLOGY",
  version: "",
  createDate: "",
  changeDate: "",
});

const createSynapseKindNeuralProfileEntry = (): SynapseKindNeuralProfileEntry => ({
  id: createTempId("neural-profile"),
  synapseKindNeuralProfileId: "",
  synapseKindId: "",
  baseWeightMin: "",
  baseWeightMax: "",
  baseDelayMinMs: "",
  baseDelayMaxMs: "",
  basePlasticityRule: "",
  canAdaptWeights: "false",
  canAdaptDelays: "false",
  allowedOverrideFields: "",
  validationRanges: "",
});

interface SynapseKindFormProps {
  synapseKinds: any[];
  onClose: () => void;
  onSuccess?: (synapseKind: any) => void;
}

const SynapseKindForm: React.FC<SynapseKindFormProps> = ({ synapseKinds, onClose, onSuccess }) => {
  const [synapseKindFormState, setSynapseKindFormState] = useState<SynapseKindFormState>(
    () => createSynapseKindFormState()
  );
  const [synapseKindMatrixRows, setSynapseKindMatrixRows] = useState<SynapseKindMatrixEntry[]>(
    [createSynapseKindMatrixRow()]
  );
  const [synapseKindNeuralProfiles, setSynapseKindNeuralProfiles] = useState<
    SynapseKindNeuralProfileEntry[]
  >([createSynapseKindNeuralProfileEntry()]);
  const [nodeKinds, setNodeKinds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const fetchNodeKinds = async () => {
    try {
      const kindsResponse = await apiClient.get("/nodes/options/node-kinds");
      if (kindsResponse.data.success) {
        setNodeKinds(kindsResponse.data.kinds || []);
      }
    } catch (error) {
      console.error("Failed to load node kinds:", error);
    }
  };

  useEffect(() => {
    fetchNodeKinds();
  }, []);

  const handleSynapseKindFormFieldChange = (
    field: keyof SynapseKindFormState,
    value: string
  ) => {
    setSynapseKindFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSynapseKindMatrixChange = (
    rowId: string,
    field: keyof SynapseKindMatrixEntry,
    value: string
  ) => {
    setSynapseKindMatrixRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              [field]: value,
            }
          : row
      )
    );
  };

  const handleSynapseKindNeuralProfileChange = (
    profileId: string,
    field: keyof SynapseKindNeuralProfileEntry,
    value: string
  ) => {
    setSynapseKindNeuralProfiles((prev) =>
      prev.map((profile) =>
        profile.id === profileId
          ? {
              ...profile,
              [field]: value,
            }
          : profile
      )
    );
  };

  const addMatrixRow = () => {
    setSynapseKindMatrixRows((prev) => [...prev, createSynapseKindMatrixRow()]);
  };

  const removeMatrixRow = (rowId: string) => {
    setSynapseKindMatrixRows((prev) =>
      prev.length === 1 ? prev : prev.filter((row) => row.id !== rowId)
    );
  };

  const addNeuralProfile = () => {
    setSynapseKindNeuralProfiles((prev) => [
      ...prev,
      createSynapseKindNeuralProfileEntry(),
    ]);
  };

  const removeNeuralProfile = (profileId: string) => {
    setSynapseKindNeuralProfiles((prev) =>
      prev.length === 1 ? prev : prev.filter((profile) => profile.id !== profileId)
    );
  };

  const resetSynapseKindBuilder = () => {
    setSynapseKindFormState(createSynapseKindFormState());
    setSynapseKindMatrixRows([createSynapseKindMatrixRow()]);
    setSynapseKindNeuralProfiles([createSynapseKindNeuralProfileEntry()]);
  };

  const handleSubmitSynapseKind = async () => {
    // Validate required fields
    if (!synapseKindFormState.kindName || !synapseKindFormState.kindName.trim()) {
      setSubmitError("Kind name is required");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      // Helper to parse JSON strings safely
      const parseJson = (str: string, fallback: any = null) => {
        if (!str || !str.trim()) return fallback;
        try {
          return JSON.parse(str);
        } catch {
          return fallback;
        }
      };

      // Helper to convert string to number
      const toNumber = (str: string): number | null => {
        if (!str || !str.trim()) return null;
        const num = Number(str);
        return isNaN(num) ? null : num;
      };

      // Helper to convert string to boolean
      const toBoolean = (str: string): boolean | null => {
        if (str === "true") return true;
        if (str === "false") return false;
        return null;
      };

      // Helper to convert null to undefined for TypeScript compatibility
      const nullToUndefined = <T,>(value: T | null): T | undefined => {
        return value === null ? undefined : value;
      };

      // Format matrix entries (synapseKindId will be set to the newly created synapse kind on backend)
      const matrixEntries = synapseKindMatrixRows
        .filter((row) => row.sourceNodeKindId && row.targetNodeKindId)
        .map((row) => ({
          sourceNodeKindId: row.sourceNodeKindId,
          targetNodeKindId: row.targetNodeKindId,
          purpose: nullToUndefined(row.purpose?.trim() || null),
          defaultSettings: parseJson(row.defaultSettings, null),
          scope: row.scope || "ONTOLOGY",
          version: toNumber(row.version),
        }));

      // Format neural profile entries
      const neuralProfileEntries = synapseKindNeuralProfiles
        .map((profile) => ({
          baseWeightMin: toNumber(profile.baseWeightMin),
          baseWeightMax: toNumber(profile.baseWeightMax),
          baseDelayMinMs: toNumber(profile.baseDelayMinMs),
          baseDelayMaxMs: toNumber(profile.baseDelayMaxMs),
          basePlasticityRule: nullToUndefined(profile.basePlasticityRule?.trim() || null),
          canAdaptWeights: toBoolean(profile.canAdaptWeights) ?? false,
          canAdaptDelays: toBoolean(profile.canAdaptDelays) ?? false,
          allowedOverrideFields: parseJson(profile.allowedOverrideFields, null),
          validationRanges: parseJson(profile.validationRanges, null),
        }));

      const synapseKindPayload = {
        synapseKind: {
          kindName: synapseKindFormState.kindName.trim(),
          description: nullToUndefined(synapseKindFormState.description?.trim() || null),
        },
        matrix: matrixEntries.length > 0 ? matrixEntries : undefined,
        neuralProfiles: neuralProfileEntries.length > 0 ? neuralProfileEntries : undefined,
      };

      // Call the API
      const response = await apiService.createSynapseKind(synapseKindPayload) as {
        success: boolean;
        message?: string;
        synapseKind?: any;
        synapseKindId?: string;
      };

      if (response.success) {
        setSubmitSuccess(true);
        // Reset form after successful submission
        resetSynapseKindBuilder();
        
        // Call onSuccess callback if provided (e.g., to refresh synapse kinds list)
        if (onSuccess) {
          onSuccess(response.synapseKind);
        }

        // Close the form after a short delay to show success message
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setSubmitError(response.message || "Failed to create synapse kind");
      }
    } catch (error: any) {
      console.error("Error creating synapse kind:", error);
      setSubmitError(
        error.response?.data?.message ||
        error.message ||
        "An error occurred while creating the synapse kind"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="node-kind-builder">
      <div className="node-kind-builder__header">
        <div>
          <p className="node-kind-builder__eyebrow">Connection Kind Designer</p>
          <p className="node-kind-builder__subtitle">
            Create a new connection kind by providing its name and optional description.
          </p>
        </div>
        <div className="node-kind-builder__header-actions">
          <Button type="button" variant="ghost" onClick={resetSynapseKindBuilder}>
            Reset form
          </Button>
          <button
            type="button"
            className="node-kind-builder__close-icon"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="node-kind-builder__section">
        <div className="node-kind-builder__grid">
          <TextInput
            id="new-synapsekind-kindName"
            name="kindName"
            type="text"
            value={synapseKindFormState.kindName}
            onChange={(event) =>
              handleSynapseKindFormFieldChange("kindName", event.target.value)
            }
            placeholder="exc, inh, hebb, router, temporal, gate_and"
            variant="modal"
            label="KindName *"
          />
          <Textarea
            id="new-synapsekind-description"
            name="description"
            rows={4}
            value={synapseKindFormState.description}
            onChange={(event) =>
              handleSynapseKindFormFieldChange("description", event.target.value)
            }
            placeholder="Human readable description of this connection kind"
            variant="modal"
            label="Description"
          />
        </div>
      </div>

      <div className="node-kind-builder__section">
        <div className="node-kind-builder__section-header">
          <div>
            <p className="node-kind-builder__section-eyebrow">
              ConnectionKindMatrix (source ⇢ target)
            </p>
            <h4 className="node-kind-builder__section-title">Matrix entries</h4>
            <p className="node-kind-builder__section-description">
              Define which node kinds can connect via this connection kind and their default settings.
            </p>
          </div>
        </div>
        <div className="node-kind-array-list">
          {synapseKindMatrixRows.map((row, index) => (
            <div key={row.id} className="node-kind-array-card">
              <div className="node-kind-array-card__header">
                <p className="node-kind-array-card__title">
                  Matrix entry #{index + 1}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={synapseKindMatrixRows.length === 1}
                  onClick={() => removeMatrixRow(row.id)}
                >
                  Remove
                </Button>
              </div>
              <SynapseKindMatrixForm
                nodeKinds={nodeKinds}
                row={row}
                onChange={handleSynapseKindMatrixChange}
              />
            </div>
          ))}
        </div>
        <Button type="button" variant="secondary" onClick={addMatrixRow}>
          + Add matrix entry
        </Button>
      </div>

      <div className="node-kind-builder__section">
        <div className="node-kind-builder__section-header">
          <div>
            <p className="node-kind-builder__section-eyebrow">
              ConnectionKindProcessingProfile (mass + delay ranges)
            </p>
            <h4 className="node-kind-builder__section-title">Processing profile</h4>
            <p className="node-kind-builder__section-description">
              Define weight and delay ranges, plasticity rules, and adaptation capabilities.
            </p>
          </div>
        </div>
        <div className="node-kind-array-list">
          {synapseKindNeuralProfiles.map((profile, index) => (
            <div key={profile.id} className="node-kind-array-card">
              <div className="node-kind-array-card__header">
                <p className="node-kind-array-card__title">
                  Processing profile #{index + 1}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={synapseKindNeuralProfiles.length === 1}
                  onClick={() => removeNeuralProfile(profile.id)}
                >
                  Remove
                </Button>
              </div>
              <SynapseKindNeuralProfileForm
                profile={profile}
                onChange={handleSynapseKindNeuralProfileChange}
              />
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={addNeuralProfile}
        >
          + Add processing profile
        </Button>
      </div>

      <div className="node-kind-builder__action-row">
        {submitError && (
          <p className="node-kind-builder__error" style={{ color: "#dc3545", marginBottom: "1rem" }}>
            {submitError}
          </p>
        )}
        {submitSuccess && (
          <p className="node-kind-builder__success" style={{ color: "#28a745", marginBottom: "1rem" }}>
            Connection kind created successfully!
          </p>
        )}
        <p className="node-kind-builder__hint">
          {submitSuccess 
            ? "Connection kind has been created. Closing form..."
            : "Fill in the form and click Save to create a new connection kind."}
        </p>
        <div className="node-kind-builder__action-buttons">
          <Button 
            type="button" 
            variant="ghost" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Close builder
          </Button>
          <Button 
            type="button" 
            variant="primary" 
            onClick={handleSubmitSynapseKind}
            disabled={isSubmitting || submitSuccess}
          >
            {isSubmitting ? "Saving..." : submitSuccess ? "Saved!" : "Save Connection Kind"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SynapseKindForm;

